import { Check } from 'lucide-react';
import { ArrowIcon, ButtonLink } from '@/components/ui/Button';
import { Photo } from '@/components/ui/Photo';
import { useSite } from '@/features/site/SiteProvider';
import { splitHighlight } from '@/lib/utils';

export function Hero() {
  const { hero } = useSite().content;
  const [before, highlight, after] = splitHighlight(hero.titleBottom, hero.titleHighlight);

  return (
    <section aria-labelledby="hero-title" className="relative overflow-hidden bg-surface">
      <div className="container-page grid items-center gap-14 pt-12 pb-16 lg:grid-cols-12 lg:gap-10 lg:pt-16 lg:pb-24">
        <div className="lg:col-span-7 lg:pr-6">
          {hero.eyebrow && (
            <p className="eyebrow flex items-center gap-3 text-accent-dark">
              <span aria-hidden className="h-px w-8 bg-accent" />
              {hero.eyebrow}
            </p>
          )}
          <h1 id="hero-title" className="display-xl mt-6 text-primary">
            <span className="block">{hero.titleTop}</span>
            {hero.titleMuted && <span className="editorial-italic block text-muted">{hero.titleMuted}</span>}
            <span className="block">
              {before}
              {highlight && <span className="editorial-italic text-accent">{highlight}</span>}
              {after}
            </span>
          </h1>
          <p className="lede mt-7 max-w-[560px]">{hero.subtitle}</p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <ButtonLink to="/contact#inquiry" variant="accent" size="lg" icon={<ArrowIcon />}>
              {hero.primaryCtaLabel}
            </ButtonLink>
            <ButtonLink to="#capabilities" variant="outline" size="lg">
              {hero.secondaryCtaLabel}
            </ButtonLink>
          </div>

          {(hero.highlights.length > 0 || hero.stats.length > 0) && (
            <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-3 border-t border-line-strong/70 pt-6 text-[14px] text-ink-soft" aria-label="At a glance">
              {hero.highlights.map((h) => (
                <li key={h} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-accent" aria-hidden />
                  {h}
                </li>
              ))}
              {/* Only verified statistics reach the public site (filtered by the API). */}
              {hero.stats.map((s) => (
                <li key={s.label} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-accent" aria-hidden />
                  {s.label}: <strong className="font-semibold text-primary">{s.value}</strong>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="relative lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden rounded-[8px] bg-primary/10 sm:aspect-[5/4] lg:aspect-[4/5]">
            <Photo name="tailorMachine" priority sizes="(min-width: 1024px) 40vw, 100vw" position="64% 40%" />
            <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary-dark/35 via-transparent to-transparent" />
          </div>

          <div className="absolute -bottom-8 -left-4 hidden aspect-square w-[34%] overflow-hidden rounded-[8px] border-[6px] border-surface shadow-[var(--shadow-float)] sm:block lg:-left-10">
            <Photo name="threadCones" sizes="220px" position="50% 62%" />
          </div>

          {(hero.showcase.badge || hero.showcase.headline) && (
            <div className="absolute top-5 right-5 max-w-[250px] rounded-[6px] bg-white/95 px-4 py-3.5 shadow-[var(--shadow-card)] backdrop-blur sm:top-6 sm:right-6">
              {hero.showcase.badge && <p className="eyebrow text-accent-dark">{hero.showcase.badge}</p>}
              {hero.showcase.headline && <p className="mt-1.5 font-display text-[15px] leading-snug font-bold text-primary">{hero.showcase.headline}</p>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
